<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :adjustColumnsWidth="adjustColumnsWidthConfig"
      :csvButton="csvButtonConfig"
      :printButton="printButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useInstagramAccounts } from '../../composables/useInstagramAccounts';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  value: unknown;
  data?: Record<string, unknown>;
}

interface InstagramAccountRow {
  instagram_account_id: number;
  ig_username?: string;
  page_name?: string;
  franquias_nomes?: string;
  active?: boolean;
}

interface ButtonClickPayload {
  action: string;
  data?: InstagramAccountRow;
}

const emit = defineEmits<{
  'edit-instagram-account': [id: Id];
}>();

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const { removerInstagramAccount, toggleActive } = useInstagramAccounts();
const datatableRef = ref<DataTableInstance | null>(null);

const endpoint = computed<string>(() => '/index.php/instagram/accounts/lista');

const tableColumns = computed(() => [
  {
    data: 'instagram_account_id',
    title: t('ID'),
    width: 80,
    resizable: true,
    sortable: true,
    renderType: 'clickable_id',
  },
  {
    data: 'ig_username',
    title: t('Usuário Instagram'),
    width: 200,
    resizable: true,
    sortable: true,
    cellRenderer: (params: CellParams): string => `<strong>@${params.value || ''}</strong>`,
  },
  {
    data: 'page_name',
    title: t('Página Facebook'),
    width: 200,
    resizable: true,
    sortable: true,
  },
  {
    data: 'franquias_nomes',
    title: t('Franquias'),
    width: 220,
    resizable: true,
    sortable: true,
    cellRenderer: (params: CellParams): string =>
      (params.value as string) || `<span style="color:#9ca3af;">${t('Nenhuma franquia')}</span>`,
  },
  {
    data: 'token_expires_at',
    title: t('Token expira em'),
    width: 160,
    resizable: true,
    sortable: true,
  },
  {
    data: 'active',
    title: t('Ativo'),
    width: 80,
    resizable: true,
    sortable: true,
    renderType: 'toggle',
    toggleCallback: async (rowData: InstagramAccountRow, newValue: boolean): Promise<void> => {
      try {
        await toggleActive(rowData.instagram_account_id, newValue);
        eventBus.emit('globalMessage.success', newValue ? t('Conta ativada!') : t('Conta desativada!'));
      } catch (err) {
        log('InstagramAccountsTable', 'toggleActive', 'Error', err);
        eventBus.emit('globalMessage.error', t('Erro ao atualizar status'));
        eventBus.emit('instagram-accounts.refresh');
      }
    },
  },
  {
    data: null,
    title: t('Ações'),
    width: 120,
    resizable: true,
    sortable: false,
    renderType: 'action_buttons',
    buttons: [
      {
        text: t('Remover'),
        icon: 'pi pi-trash',
        class: 'p-button-danger p-button-sm',
        action: 'delete',
      },
    ],
  },
]);

const datatableConfig = computed(() => ({
  tableId: 'tabelaInstagramAccounts',
  title: t('Contas Instagram'),
  filename: `instagram_accounts_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pageLength: 100,
  height: '500px',
  footer: false,
  rowSelection: 'single',
}));

const filterConfig = computed(() => ({
  searchName: t('Procurar'),
  choosePlaceholder: t('Escolha'),
  refreshName: t('Atualizar'),
  columns: {
    instagram_account_id: 'text',
    ig_username: 'text',
    page_name: 'text',
    franquias_nomes: 'text',
    active: 'text',
  },
}));

const refreshButtonConfig       = computed(() => ({ show: true, text: t('Atualizar') }));
const adjustColumnsWidthConfig  = computed(() => ({ show: true }));
const csvButtonConfig           = computed(() => ({ show: true, text: t('CSV') }));
const printButtonConfig         = computed(() => ({ show: true, text: t('Imprimir') }));

const loadData = async (): Promise<void> => {
  if (datatableRef.value) await datatableRef.value.loadData();
};

const handleDataLoaded = (data: unknown): void => {
  log('InstagramAccountsTable', 'handleDataLoaded', 'Data loaded', data);
};

const handleDataError = (): void => {
  eventBus.emit('globalMessage.error', t('Erro ao carregar dados'));
};

const handleEditClick = (id: Id): void => {
  emit('edit-instagram-account', id);
};

const handleDelete = (instagramAccountId: number): void => {
  confirmAction({
    message: t('Tem certeza que deseja remover esta conta Instagram?'),
    header: t('Confirmar Exclusão'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async (): Promise<void> => {
      try {
        await removerInstagramAccount(instagramAccountId);
        eventBus.emit('globalMessage.success', t('Conta removida com sucesso!'));
        eventBus.emit('instagram-accounts.refresh');
      } catch (err) {
        eventBus.emit('globalMessage.error', t('Erro ao remover conta'));
      }
    },
  });
};

const handleButtonClick = ({ action, data }: ButtonClickPayload): void => {
  if (action === 'delete' && data?.instagram_account_id) handleDelete(data.instagram_account_id);
};

onMounted(() => {
  eventBus.on('instagram-accounts.refresh', loadData);
});

onBeforeUnmount(() => {
  eventBus.off('instagram-accounts.refresh', loadData);
});

defineExpose({ loadData });
</script>
